import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  fetchXeroInvoiceDetail,
  fmtXeroMoney,
  sendXeroBatchReminder,
  type SendReminderOptions,
  type XeroInvoiceRow,
} from '../../services/xeroApi';
import { useAppSettings } from '../../context/SettingsContext';
import { isWizzardConfigured, wizzardGenerateReminder, type ReminderTone } from '../../services/wizzardApi';
import { WizzardToneBar } from './WizzardToneBar';
import { defaultReminderTone } from './reminderTones';

type Props = {
  open: boolean;
  invoices: XeroInvoiceRow[];
  onClose: () => void;
  onSent?: () => void;
};

export function BatchReminderDialog({ open, invoices, onClose, onSent }: Props) {
  const { settings, isSecretConfigured } = useAppSettings();
  const collections = settings.integrations.xero.collections;
  const wizzardReady = isWizzardConfigured(settings, isSecretConfigured);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachPdf, setAttachPdf] = useState(collections?.attachPdf !== false);
  const [stripeLink, setStripeLink] = useState(collections?.stripePaymentLink !== false);
  const [reminderTone, setReminderTone] = useState<ReminderTone>('friendly');

  const total = useMemo(() => invoices.reduce((s, i) => s + i.amountDue, 0), [invoices]);
  const currency = invoices[0]?.currency || 'AUD';
  const contact = invoices[0]?.contact || 'Customer';

  useEffect(() => {
    if (!open || invoices.length === 0) {
      return;
    }
    setError(null);
    const emailFromRow = invoices[0].contactEmail || '';
    setTo(emailFromRow);
    if (!emailFromRow) {
      void fetchXeroInvoiceDetail(invoices[0].id)
        .then((detail) => setTo(detail.invoice.contactEmail || ''))
        .catch(() => undefined);
    }
    const numbers = invoices.map((i) => i.number).join(', ');
    setSubject(
      invoices.length === 1
        ? `Payment reminder — invoice ${invoices[0].number}`
        : `Payment reminder — ${invoices.length} invoices (${numbers})`
    );
    setMessage('');
    setAttachPdf(collections?.attachPdf !== false);
    setStripeLink(collections?.stripePaymentLink !== false);
    setReminderTone(defaultReminderTone(Math.max(0, ...invoices.map((i) => i.daysOverdue))));
  }, [open, invoices, collections]);

  const handleWizzardDraft = async (tone: ReminderTone = reminderTone) => {
    setDrafting(true);
    setError(null);
    setReminderTone(tone);
    try {
      const draft = await wizzardGenerateReminder({ invoiceIds: invoices.map((i) => i.id), tone });
      setSubject(draft.subject);
      setMessage(draft.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Wizzard draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    const opts: SendReminderOptions = {
      to,
      subject,
      message: message || undefined,
      attachPdf,
      includeStripePaymentLink: stripeLink,
    };
    try {
      await sendXeroBatchReminder(
        invoices.map((i) => i.id),
        opts
      );
      onSent?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send combined reminder ({invoices.length} invoice(s))</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            <strong>{contact}</strong> — total due {fmtXeroMoney(total, currency)}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {invoices.map((inv) => (
              <Typography component="li" variant="body2" key={inv.id}>
                {inv.number} — {fmtXeroMoney(inv.amountDue, inv.currency)}
              </Typography>
            ))}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {wizzardReady && (
            <WizzardToneBar
              tone={reminderTone}
              onToneChange={setReminderTone}
              onDraft={(t) => void handleWizzardDraft(t)}
              drafting={drafting}
              disabled={sending}
            />
          )}
          <TextField
            label="To"
            fullWidth
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            helperText={
              !to.trim()
                ? 'No email on this Xero contact — add a primary email or a contact person with “Include in emails” in Xero, or type an address here.'
                : undefined
            }
          />
          <TextField label="Subject" fullWidth size="small" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <TextField
            label="Message (optional — default generated if blank)"
            fullWidth
            multiline
            minRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <FormControlLabel
            control={<Checkbox checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />}
            label="Attach invoice PDFs from Xero"
          />
          <FormControlLabel
            control={<Checkbox checked={stripeLink} onChange={(e) => setStripeLink(e.target.checked)} />}
            label="Include Stripe card payment link (requires Stripe secret key)"
          />
          <Alert severity="info" sx={{ py: 0.5 }}>
            Reply-To uses a tracking address so customer replies reach your inbox. Log replies in the invoice
            correspondence trail (or set an AR alias in API Settings → Xero).
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={sending ? <CircularProgress size={18} /> : <EmailOutlinedIcon />}
          disabled={sending || !to.trim()}
          onClick={() => void handleSend()}
        >
          Send email
        </Button>
      </DialogActions>
    </Dialog>
  );
}
